import Model from "../../model/Model";
import Review, {ReviewStatus} from "../../model/Review";
import User from "../../model/User";
import * as React from "react";
import {useEffect, useState} from "react";
import {useResources} from "../../stores/ResourceProvider";
import {Box, DialogContentText, Grid, useTheme} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import FormValidator from "../../components/form/FormValidator";
import DialogContent from "@mui/material/DialogContent";
import DatePickerValidator from "../../components/form/DatePickerValidator";
import DialogActions from "@mui/material/DialogActions";
import ProgressButton from "../../components/form/ProgressButton";
import TextFieldValidator from "../../components/form/TextFieldValidator";
import Snapshot from "../../model/Snapshot";
import {createUUID, getISODateFromDate, isoToLocalDate} from "../../stores/StoreUtilities";
import config from 'react-global-configuration';
import DialogButton from "../../components/form/DialogButton";
import {
  ClientAccess,
  CreateReviewInput,
  CreateSnapshotInput,
  CreateUserSettingInput,
  SnapshotType,
  UpdateUserSettingInput
} from "../../API";
import {isSameDay, startOfYear} from "date-fns";
import {UserSettingType} from "../../model/UserSetting";

const AnnualReviewDialog = ({
  user,
  onChange
}: {
  user?: User,
  onChange(review?: Review): void
}) => {
  const [client, setClient] = useState<User | undefined>()
  const [models, setModels] = useState<Model[]>([])
  const [baseModel, setBaseModel] = useState<Model | undefined>()
  const [review, setReview] = useState<Review | undefined>()
  const [reviewDate, setReviewDate] = useState<Date | undefined>(new Date())
  const [minDate, setMinDate] = useState<Date>(startOfYear(new Date()))
  const [maxDate, setMaxDate] = useState<Date>(new Date())
  const [modelName, setModelName] = useState<string>("")
  const [copyModel, setCopyModel] = useState<boolean>(true)
  const [newModel, setNewModel] = useState<Model | undefined>()
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [baseSnapshot, setBaseSnapshot] = useState<Snapshot | undefined>()
  const id = createUUID()
  const [managed, setManaged] = useState<boolean>(false)
  const [reviewId, setReviewId] = useState<string>(id)
  const [reviewLink, setRevierwLink] =
    useState<string | undefined>(`${config.get("baseUrl")}/review/${id}`)
  const [expandClientSearch, setExpandClientSearch] = useState<boolean>(false)
  const [message, setMessage] = useState<string | undefined>()
  const [isProcessing, setIsProcessing] = useState<boolean>(false)

  const {modelStore, userStore, notify} = useResources()
  const theme = useTheme()
  
  const styles = {
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "1.0em",
      maxHeight: 15,
      marginBottom: "6px",
      textWrap: "nowrap",
      color: theme.palette.grey["700"]
    },
  }

  useEffect(() => {
    if (user) {
      loadClient(user)
    }
  }, [user])
  
  const loadClient = async (user?: User) => {
    if (!user) {
      return
    }
    if (user.models.length === 0) {
      user = await userStore.getUser(user.id)
      if (!user) {
        return
      }
    }
    setClient(user)
    setModels(user.models)

    let primaryModel: Model | undefined
    if (user.primaryModelId) {
      primaryModel = user!.models.find((m: Model) => m.id === user!.primaryModelId)
      if (primaryModel) {
        loadModel(primaryModel)
      }
    }

    const date = new Date()
    setReviewDate(startOfYear(date))
    setManaged(false)
  }

  const loadModel = async (model: Model) => {
    const fullModel = await modelStore.getCurrentModel(model.id, true)
    if (fullModel) {
      setBaseModel(fullModel)
    }
  }

  const handleSubmit = async () => {
    if (!client || !baseModel || !reviewDate) {
      return
    }

    try {
      setMessage(undefined)
      setIsProcessing(true)

      let reviewModel: Model | undefined
      let baseSnap: Snapshot | undefined
      let reviewSnapshot: Snapshot | undefined

      // See if a review with this date already exists
      const findDate = getISODateFromDate(reviewDate)
      const review = client.reviews.find((r: Review) => r.reviewDate === findDate )
      if (review) {
        setMessage("A review with this date already exists.")
        return
      }

      reviewModel = baseModel
      modelStore.setCurrentModel(reviewModel)
      // Find or create snapshot
      reviewSnapshot = reviewModel.snapshots.find((s: Snapshot) => isSameDay(s.date, reviewDate))
      if (reviewSnapshot) {
        reviewSnapshot = await modelStore.updateSnapshotBudget(reviewSnapshot, reviewModel)
      } else {
        // Create new snapshot
        // Get latest details
        const details = modelStore.buildSnapshotDetails(reviewModel, reviewDate, SnapshotType.Combined)
        const snapshotInput: CreateSnapshotInput = {
          userId: client.id,
          accountId: client.accountId,
          modelId: baseModel.id,
          date: getISODateFromDate(reviewDate),
          description: `Annual Review ${reviewDate.getFullYear()}`,
          snapshotType: SnapshotType.Combined,
          details: JSON.stringify(details)
        }
        reviewSnapshot = await modelStore.createSnapshot(snapshotInput)
      }

      if (reviewModel && reviewSnapshot) {
        const input: CreateReviewInput = {
          id: reviewId,
          userId: client.id,
          accountId: client.accountId,
          modelId: reviewModel.id,
          baseSnapId: reviewSnapshot.id,
          snapshotId: reviewSnapshot.id,
          reviewDate: getISODateFromDate(reviewDate),
          checklist: JSON.stringify(Review.getDefaultChecklist(false)),
          managed: managed,
          status: ReviewStatus.Pending
        }

        const review = await userStore.createReview(input)
        if (review) {
          await updateUserSetting(ReviewStatus.Pending)
          onChange(review)
        }
      }
    } catch (err: any) {
      notify.show("error", `Error creating new review (${err.message})`)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleRemindMe = async () => {
    await updateUserSetting(ReviewStatus.Pending)
    onChange(undefined)
  }

  const handleDecline = async () => {
    await updateUserSetting(ReviewStatus.Declined)
    onChange(undefined)
  }

  const updateUserSetting = async (status: ReviewStatus) => {
    const user = userStore.user!
    const year = reviewDate!.getFullYear().toString()
    let userSetting = user.getUserSetting(UserSettingType.annualReview, year)
    if (userSetting) {
      userSetting.settings["status"] = status
      const input: UpdateUserSettingInput = {
        id: userSetting.id,
        settings: JSON.stringify(userSetting.settings)
      }
      const update = await userStore.updateUserSetting(input)
      if (update) {
        user.updateUserSetting(update)
      }
    } else {
      const input: CreateUserSettingInput = {
        userId: user.id,
        accountId: user.accountId,
        name: year,
        settingType: UserSettingType.annualReview,
        settings: JSON.stringify({status: status})
      }
      const update = await userStore.createUserSetting(input)
      if (update) {
        user.updateUserSetting(update)
      }
    }
  }

  return (
    <Dialog
      id="annualReviewDialog"
      open={true}
      onClose={() => onChange()}
      maxWidth="sm"
      fullWidth
      aria-labelledby="annualReviewDialogTitle"
    >
      <FormValidator onSubmit={handleSubmit} autoComplete="off">
        <DialogTitle id="annualReviewDialogTitle">Annual Review</DialogTitle>
        <DialogContent sx={{my:2}}>
          <DialogContentText>
            It is the beginning of the year and time to review your Retirement Timeline, Assets & Liabilities, and Budget items.
            The following steps are recommended:
          </DialogContentText>
          <Grid container spacing={1} mt={1}>
            <Grid item xs={8}>
              <TextFieldValidator
                type="text"
                validators={{ required: true }}
                name="baseModel"
                label="Current Model"
                variant="standard"
                autocompleteOptions={{
                  freeSolo: false,
                  options: models,
                  getOptionLabel: (option: Model) => option && option.name ? option.name : "None",
                  isOptionEqualToValue: (option: Model, value: Model) => {
                    return (option.id === value.id)
                  },
                  value: baseModel ?? null,
                  disableClearable: true,
                  onChange: (event: any, value: Model, reason: any) => {
                    loadModel(value)
                  }
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <DatePickerValidator
                name="reviewDate"
                label="Review Date"
                value={reviewDate}
                minDate={minDate}
                maxDate={maxDate}
                format="MM-dd-yyyy"
                variant="standard"
                margin="dense"
                fullWidth
                required
                onChange={(newValue: any) => {
                  setReviewDate(newValue)
                }}
              />
            </Grid>
          </Grid>
          {message &&
            <DialogContentText sx={{color: theme.palette.error.main, mt: 1}}>
              {message}
            </DialogContentText>
          }
        </DialogContent>
        <DialogActions>
          <Box display="flex" justifyContent="flex-end">
            <DialogButton variant="secondary" spacing="right" onClick={handleRemindMe}>
              Remind Me Later
            </DialogButton>
            <DialogButton variant="secondary" spacing="right" onClick={handleDecline}>
              Decline Review
            </DialogButton>
            <ProgressButton variant="contained" size="medium" color="primary" sx={{ color: "#fff" }}
                            type="submit" processing={isProcessing}>
              Start Review
            </ProgressButton>
          </Box>
        </DialogActions>
      </FormValidator>
    </Dialog>
  )
}

export default AnnualReviewDialog